import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import { readQuery } from '../query';
import ListTools from '../components/ListTools';
import Guid from '../components/Guid';
import Pager from '../components/Pager';

/**
 * Users — one search box over everything, as the design has it.
 *
 * The endpoint takes the query in the *path*
 * (`/admin/users/search/{key}`), not as a parameter, and matches it against id,
 * name, email and phone all at once. So there is nothing to build a filter row
 * out of: one field is not a simplification of the old page, it is the shape of
 * the API. The placeholder says which four things it searches, because otherwise
 * nobody would guess.
 *
 * No total comes back — the response is a bare array — so the pager offers a next
 * page whenever the current one came back full, the same guess the old admin
 * makes. The design's "Pagina 1 di 86.234" would need a count the endpoint does
 * not return.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

// The payload answers both casings depending on the endpoint, so both keys are
// listed and whichever is present wins per row. No chips here: this list has one
// filter and it is the field you are looking at.
const EXPORT_COLUMNS = [
  ['Id', 'Id'],
  ['Name', 'Name'],
  ['Email', 'Email'],
  ['PhoneNumber', 'Phone'],
  ['CreatedAtCet', 'Created (CET)']
];

const UsersInternal = ({ api }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const query = useMemo(() => readQuery(location.search), [location.search]);

  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(query.q ?? '');

  const page = parseInt(query.page, 10) || 1;
  const perPage = parseInt(query.resultsPerPage, 10) || 15;

  useEffect(() => {
    setDraft(query.q ?? '');
  }, [query.q]);

  const load = useCallback(async () => {
    setRows(null);
    setFailed(false);
    try {
      const data = await api.users.searchUsers({
        key: query.q || '',
        skipPages: page,
        resultsCount: perPage
      });
      setRows(Array.isArray(data) ? data : (data?.Users ?? data?.users ?? []));
    } catch (error) {
      setFailed(true);
    }
  }, [api, query.q, page]);

  useEffect(() => {
    load();
  }, [load]);

  const search = value => {
    const params = [value ? `q=${encodeURIComponent(value)}` : null].filter(Boolean).join('&');
    navigate(params ? `?${params}` : '');
  };

  return (
    <div className="next-page next-page--wide">
      <header className="next-page__header next-page__header--row">
        <div>
          <h1>Users</h1>
          <p className="next-muted">
            {rows === null
              ? 'Loading…'
              : `Page ${page} · ${rows.length} row${rows.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="next-page__actions">
          <button type="button" className="next-btn" onClick={load}>Refresh</button>
        </div>
      </header>

      <section className="next-card">
        <form
          className="next-filters"
          onSubmit={event => {
            event.preventDefault();
            search(draft.trim());
          }}
        >
          <div className="next-filters__row">
            <label className="next-filter next-filter--wide">
              <span className="next-filter__label">Search</span>
              <input
                className="next-input"
                value={draft}
                placeholder="Id, name, email or phone…"
                onChange={event => setDraft(event.target.value)}
              />
            </label>
            <div className="next-filters__buttons">
              <button type="submit" className="next-btn next-btn--primary">Search</button>
              {query.q && (
                <button
                  type="button"
                  className="next-btn next-btn--quiet"
                  onClick={() => { setDraft(''); search(''); }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </form>
      </section>

      <ListTools
        screenPath="/next/users"
        search={location.search}
        columns={EXPORT_COLUMNS}
        rows={(rows ?? []).map(row => ({
          Id: row.Id ?? row.id,
          Name: row.Name ?? row.name,
          Email: row.Email ?? row.email,
          PhoneNumber: row.PhoneNumber ?? row.phoneNumber,
          CreatedAtCet: row.CreatedAtCet ?? row.createdAtCet
        }))}
        exportName="users"
        perPage={perPage}
        fetchPage={next => api.users
          .searchUsers({ key: query.q || '', skipPages: next, resultsCount: perPage })
          .then(data => (Array.isArray(data) ? data : (data?.Users ?? data?.users ?? [])))}
      />

      {failed ? (
        <div className="next-note next-note--error">
          Could not load the users.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      ) : (
        <section className="next-card">
          <div className="next-table-wrap">
            <table className="next-table next-table--wide next-table--stack">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Created at (CET)</th>
                </tr>
              </thead>
              <tbody>
                {rows === null && (
                  <tr><td colSpan={5} className="next-muted next-table__note">Loading…</td></tr>
                )}
                {rows !== null && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="next-muted next-table__note">
                      {query.q ? 'No user matches that search.' : 'Search for a user to begin.'}
                    </td>
                  </tr>
                )}
                {(rows ?? []).map(row => (
                  <tr key={row.Id || row.id}>
                    <td data-label=""><Guid value={row.Id || row.id} to={`/next/user/${row.Id || row.id}`} /></td>
                    <td className="next-stack__main" data-label="Name">{row.Name || row.name || '—'}</td>
                    <td className="next-break" data-label="Email">{row.Email || row.email || '—'}</td>
                    <td className="next-nowrap" data-label="Phone">{row.PhoneNumber || row.phoneNumber || '—'}</td>
                    <td className="next-nowrap next-muted" data-label="Created (CET)">
                      {fmtDate(row.CreatedAtCet || row.createdAtCet || row.CreatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            perPage={perPage}
            // Keeps whatever was searched — dropping it here would clear the
            // list as a side effect of asking to see more of it.
            onPerPageChange={size => navigate(`?${[
              query.q ? `q=${encodeURIComponent(query.q)}` : null,
              size === 15 ? null : `resultsPerPage=${size}`
            ].filter(Boolean).join('&')}`)}
            page={page}
            hasNext={(rows ?? []).length >= perPage}
            onChange={next => {
              const params = [
                query.q ? `q=${encodeURIComponent(query.q)}` : null,
                next > 1 ? `page=${next}` : null
              ].filter(Boolean).join('&');
              navigate(params ? `?${params}` : '');
            }}
          />
        </section>
      )}
    </div>
  );
};

UsersInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Users = withApi(UsersInternal);

Users.requiresLogin = true;

export default Users;
